/**
 * Persist draft power distribution rows created before the parent request is saved (#996 Phase 2).
 * Mirrors largeUpsConfigurationDraft.
 */

import { api } from './api';
import { networkService } from './network';
import { offlineCache } from './offlineCache';
import { toCreatePowerDistributionPayload } from './powerDistributionPayload';
import {
  stableDraftInstanceId,
  withDraftDimensionUnitTempId,
} from '../request/dimensionUnitChoices';

import type { PowerDistributionConfiguration } from '../types/powerDistribution';

export async function persistDraftPowerDistributionConfigurations(
  requestId: string | number,
  units: PowerDistributionConfiguration[],
): Promise<void> {
  const createdIds: number[] = [];
  const idKey = String(requestId);

  try {
    for (const unit of units) {
      const created = await api.post<PowerDistributionConfiguration>(
        `/requests/${requestId}/power-distribution`,
        withDraftDimensionUnitTempId(
          unit.id,
          toCreatePowerDistributionPayload(unit),
        ),
      );
      if (created.id) {
        createdIds.push(created.id);
      }
    }
  } catch (error) {
    for (const powerDistributionId of createdIds) {
      try {
        await api.delete(
          `/requests/${idKey}/power-distribution/${powerDistributionId}`,
        );
      } catch (rollbackError) {
        console.error(
          'Failed to roll back power distribution after partial draft flush:',
          rollbackError,
        );
      }
    }
    throw error;
  }
}

/**
 * Flush draft power distribution after Create Request save — online POST or offline cache + queue.
 */
export async function flushDraftPowerDistributionConfigurations(
  requestId: string | number,
  units: PowerDistributionConfiguration[],
  isOfflineRequest = false,
): Promise<void> {
  if (!units.length) {
    return;
  }

  const id = String(requestId);

  if (isOfflineRequest || !networkService.isConnected()) {
    const normalized = units.map((unit, index) => ({
      ...toCreatePowerDistributionPayload(unit),
      id: stableDraftInstanceId(unit.id, index),
      requestId: typeof requestId === 'number' ? requestId : 0,
      displayOrder: unit.displayOrder ?? index + 1,
      status: unit.status ?? 'new',
    })) as PowerDistributionConfiguration[];

    await offlineCache.updateCachedRequestPowerDistribution(id, normalized);
    for (const unit of normalized) {
      await offlineCache.addPowerDistributionToOfflineQueue(
        id,
        'create',
        withDraftDimensionUnitTempId(
          unit.id,
          toCreatePowerDistributionPayload(unit),
        ),
        unit.id,
      );
    }
    return;
  }

  await persistDraftPowerDistributionConfigurations(requestId, units);
}
